import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { TextInput } from '@tecuity/granite-ui/TextInput'
import { DatePicker } from '@tecuity/granite-ui/DatePicker'
import { SelectBox } from '@tecuity/granite-ui/SelectBox'
import { SET_CORRESPONDENCE2_REDUCER } from '../Correspondence2Reducer'
import { Spinner } from 'components/Loaders/Loaders'
import IconCalendar from '@tecuity/sos-icons/react/calendar'
import format from 'date-fns/format'
import { GET_BUSINESS_RECORD_SUB_TYPE_XREFS_BY_RECORD_TYPE, GET_MISCELLANEOUS_MODULE_FILING_TYPES } from '../../../requests'
import isValid from 'date-fns/isValid'
import { useAppSetting } from '../../../Utilities/hooks'
import { FilingSourceTypeIds } from '../../../Utilities/Utilities'

const FilingInfoStep = () => {
  const allowCorrespondenceDateChange = useAppSetting('allowCorrespondenceDateChange')
  const dispatch = useDispatch()
  const corrInfo = useSelector((state) => state.correspondence2, shallowEqual)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const {
    readOnly,
    sourceTypes,
    recordTypes,
    recordSubTypes,
    recordSubTypesXref,
    filingTypes,
    SourceTypeId,
    RecordTypeId,
    RecordSubTypeId,
    FilingTypeId,
    MiscellaneousModuleId,
    miscellaneousModules,
    CustomCorrespondenceTypeId,
    showFilingSpinner,
  } = corrInfo

  const isCorrection = useMemo(() => {
    return CustomCorrespondenceTypeId === 2
  }, [CustomCorrespondenceTypeId])

  const sourceTypeOptions = useMemo(() => {
    if (!Array.isArray(sourceTypes)) {
      // console.error('sourceTypes not valid')
      return []
    }
    return sourceTypes.map((type) => {
      return { label: type.Name, value: type.Id }
    })
  }, [sourceTypes])

  const recordTypeOptions = useMemo(() => {
    if (!Array.isArray(recordTypes)) {
      // console.error('recordTypes not valid')
      return []
    }
    return recordTypes.map((type) => {
      return { value: type.Id, label: type.Name }
    })
  }, [recordTypes])

  const recordSubTypeOptions = useMemo(() => {
    if (!Array.isArray(recordSubTypes)) {
      // console.error('recordSubTypes not valid')
      return []
    }
    if (!Array.isArray(recordSubTypesXref) || recordSubTypesXref.length === 0) return []
    return recordSubTypes
      .filter((type) => recordSubTypesXref.includes(type.Id))
      .map((type) => {
        return { value: type.Id, label: type.Name }
      })
  }, [recordSubTypes, recordSubTypesXref])

  const filingTypeOptions = useMemo(() => {
    if (!Array.isArray(filingTypes)) {
      return []
    }
    return filingTypes.map((type) => {
      return { value: type.Id, label: type.Name }
    })
  }, [filingTypes])

  const isMiscellaneous = useMemo(() => +SourceTypeId === FilingSourceTypeIds.Miscellaneous, [SourceTypeId])

  const miscellaneousModuleOptions = useMemo(
    () => (miscellaneousModules || []).map((m) => ({ value: m.MiscellaneousModuleId, label: m.DisplayName })),
    [miscellaneousModules]
  )

  useEffect(() => {
    if (RecordTypeId && recordTypeOptions.length > 0) {
      const recordType = recordTypeOptions.find((type) => +type.value === +RecordTypeId)
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          RecordType: recordType ? recordType.label : '',
        },
      })
    }
  }, [RecordTypeId, recordTypeOptions])

  useEffect(() => {
    const sourceType = sourceTypes.find((type) => type.Id === +SourceTypeId)
    if (sourceType && sourceType.ModuleName === 'Business' && RecordTypeId) {
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: { showFilingSpinner: true },
      })
      GET_BUSINESS_RECORD_SUB_TYPE_XREFS_BY_RECORD_TYPE(
        RecordTypeId,
        (data) => {
          // console.log(data)
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: {
              recordSubTypesXref: data.BusinessRecordSubTypeIds,
              showFilingSpinner: false,
            },
          })
        },
        (err) => {
          console.error(err)
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: { showFilingSpinner: false },
          })
        }
      )
    }
  }, [RecordTypeId, SourceTypeId, sourceTypes])

  useEffect(() => {
    if (RecordSubTypeId && recordSubTypeOptions.length > 0) {
      const RecordSubType = recordSubTypeOptions.find((type) => +type.value === +RecordSubTypeId)
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          RecordSubType: RecordSubType ? RecordSubType.label : '',
        },
      })
    }
  }, [RecordSubTypeId, recordSubTypeOptions])

  useEffect(() => {    
    if (FilingTypeId && filingTypeOptions.length > 0) {
      const filingType = filingTypeOptions.find((type) => +type.value === +FilingTypeId)
      dispatch({
        type: SET_CORRESPONDENCE2_REDUCER,
        payload: {
          FilingType: filingType ? filingType.label : '',
        },
      })
    }
  }, [FilingTypeId, filingTypeOptions])

  useEffect(() => {
    if (isMiscellaneous && MiscellaneousModuleId) {      
      GET_MISCELLANEOUS_MODULE_FILING_TYPES(
        MiscellaneousModuleId,
        (data) => {
          dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { filingTypes: data } })
        },
        console.error
      )
    } else if (isMiscellaneous && !MiscellaneousModuleId) {
      dispatch({ type: SET_CORRESPONDENCE2_REDUCER, payload: { filingTypes: [], FilingTypeId: null, FilingType: '' } })
    }
  }, [MiscellaneousModuleId, isMiscellaneous])

  return (
    <div className="corr-field-container">
      {corrInfo.EntryType === 'Paper Filed' && (
        <TextInput
          label={
            <span>
              <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Entity Name
            </span>
          }
          onChange={(e) => {
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { RecordName: e.target.value },
            })
          }}
          disabled={readOnly ? true : undefined}
          value={corrInfo.RecordName || ''}
        />
      )}
      {allowCorrespondenceDateChange.setting &&
      allowCorrespondenceDateChange.setting.VALUE === true ? (
        <div className="date-picker-holder">
          <TextInput
            label={
              <span>
                <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Correspondence Date
              </span>
            }
            placeholder="MM/DD/YYYY"
            onChange={(e) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { FilingDate: format(e.target.value, 'MM/dd/yyyy HH:mm a') },
              })
            }}
            value={corrInfo.FilingDate.split(' ')[0]}
            disabled={readOnly ? true : undefined}
          />
          <button
            className={`btn btn-primary btn-raised picker-button`}
            aria-label={`Select Todays Date`}
            onClick={(e) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { FilingDate: format(new Date(), 'MM/dd/yyyy HH:mm a') },
              })
            }}
            disabled={readOnly ? true : undefined}
          >
            Today
          </button>
          <button
            className={`btn btn-primary btn-raised picker-button `}
            aria-label={`Open calendar widget for Today's Date`}
            onClick={() => {
              setShowDatePicker(true)
            }}
            disabled={readOnly ? true : undefined}
          >
            <IconCalendar size="small" style={readOnly ? { backgroundColor: '#999999' } : {}} />
          </button>
          <DatePicker
            onChange={(e) => {
              dispatch({
                type: SET_CORRESPONDENCE2_REDUCER,
                payload: { FilingDate: format(new Date(e), 'MM/dd/yyyy HH:mm a') },
              })
            }}
            isOpen={showDatePicker}
            disabled={readOnly ? true : undefined}
            onCalendarClose={() => setShowDatePicker(false)}
          />
        </div>
      ) : (
        <div className="correspondence-date">
          <label>Correspondence Date</label>
          <div>{corrInfo.FilingDate}</div>
        </div>
      )}
      <SelectBox
        label={
          <span>
            <span style={{ color: `hsl(345,100%,42%)` }}>*</span>Source Type
          </span>
        }
        value={SourceTypeId || ''}
        onChange={(e) => {
          const descr = sourceTypeOptions.find((opt) => {
            return +opt.value === +e
          })
          dispatch({
            type: SET_CORRESPONDENCE2_REDUCER,
            payload: {
              SourceTypeId: e,
              SourceType: descr ? descr.label : '',
              RecordTypeId: null,
              RecordType: '',
              RecordSubTypeId: null,
              RecordSubType: '',
              recordSubTypesXref: [],
              FilingTypeId: null,
              FilingType: '',
            },
          })
        }}
        options={sourceTypeOptions}
        disabled={readOnly ? true : undefined}
      />      
      {isMiscellaneous && (
        <SelectBox
          label={
            <span>
              { isCorrection ? <span style={{ color: `hsl(345,100%,42%)` }}>*</span> : null}Miscellaneous Module
            </span>
          }
          value={MiscellaneousModuleId || ''}
          onChange={(e) => {
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { MiscellaneousModuleId: e, FilingTypeId: null, FilingType: '' },
            })
          }}
          options={miscellaneousModuleOptions}
          disabled={readOnly ? true : undefined}
        />
      )}
      {recordTypeOptions.length > 0 && (
        <SelectBox
          label={
            <span>
              {isCorrection ? <span style={{ color: `hsl(345,100%,42%)` }}>*</span> : null}Record
              Type
            </span>
          }
          value={corrInfo.RecordTypeId || ''}
          onChange={(e) => {
            const descr = recordTypeOptions.find((opt) => {
              return +opt.value === +e
            })
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { RecordTypeId: e, RecordType: descr ? descr.label : '' },
            })
          }}
          options={recordTypeOptions}
          disabled={readOnly ? true : undefined}
        />
      )}
      {recordSubTypeOptions.length > 0 && (
        <SelectBox
          noPlaceholder
          label={
            <span>
              {isCorrection ? <span style={{ color: `hsl(345,100%,42%)` }}>*</span> : null}Record
              Sub Type
            </span>
          }
          value={corrInfo.RecordSubTypeId || ''}
          onChange={(e) => {
            const descr = recordSubTypeOptions.find((opt) => {
              return +opt.value === +e
            })
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { RecordSubTypeId: e, RecordSubType: descr ? descr.label : '' },
            })
          }}
          options={[{ label: 'None', value: null }, ...recordSubTypeOptions]}
          disabled={readOnly ? true : undefined}
        />
      )}
      {(isMiscellaneous || filingTypeOptions.length > 0) && (
        <SelectBox
          label={
            <span>
              {isCorrection ? <span style={{ color: `hsl(345,100%,42%)` }}>*</span> : null}Filing
              Type
            </span>
          }
          value={corrInfo.FilingTypeId || ''}
          onChange={(e) => {
            const descr = filingTypeOptions.find((opt) => {
              return +opt.value === +e
            })
            dispatch({
              type: SET_CORRESPONDENCE2_REDUCER,
              payload: { FilingTypeId: e, FilingType: descr ? descr.label : '' },
            })
          }}
          options={filingTypeOptions}
          disabled={readOnly ? true : undefined}
        />
      )}
      {showFilingSpinner && <Spinner color="#7792d0" className="entity-step-loader" size="small" />}
    </div>
  )
}

export default FilingInfoStep

export const isFilingInfoStepValid = (corrInfo) => {
  // const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|1\d|2\d|3[01])\/(19|20)\d{2}$/ // MM/dd/yyyy
  const {
    SourceTypeId,
    FilingDate,
    CustomCorrespondenceTypeId,
    recordTypes,
    filingTypes,
    RecordTypeId,
    FilingTypeId,
    showFilingSpinner,
    EntryType,
    RecordName,
  } = corrInfo
  const isMisc = +corrInfo.SourceTypeId === FilingSourceTypeIds.Miscellaneous
  if (showFilingSpinner) return false
  if (EntryType === 'Paper Filed' && !RecordName) return false
  if (CustomCorrespondenceTypeId === 2) {
    // Corrections require RecordType and FilingType if they have types for 90 day rejection job to balance receipts
    if (isMisc) {
      if (!corrInfo.MiscellaneousModuleId) return false
    } else {
      if (Array.isArray(recordTypes) && recordTypes.length > 0 && !RecordTypeId) return false
    }
    if (Array.isArray(filingTypes) && filingTypes.length > 0 && !FilingTypeId) return false
  }  

  return isValid(new Date(FilingDate.split(' ')[0])) && SourceTypeId
}
